/**
 * D883 (program D871 phase 5, ADR-0160): the provider service lines in the Admin - the in-app confirmed moves, the drawer of one request in Serviços em análise,
 * and the "Serviços" card of the provider's record (the lines, each vehicle's approval per line, the duty session and the history of both audits).
 *
 * Every move goes through `useConsole().confirm` (never a native dialog) with the revision the screen read; a refusal is said through the surface's words
 * (`refusal.admin.provider_services`, else the system sentence of its reason code) and the record is read again. A move the operator's words do not allow is not
 * drawn (the server re-authorizes anyway). No code reaches the screen: lines, states, events, reasons, vehicle classes and document types are words.
 */
import { type ReactElement, type ReactNode, useCallback } from 'react';
import { type ApiError, type AdminProviderServicesView, type AdminServiceDeltaView, type AdminServiceQueueRowView } from '@vello/api-client';
import { type Result } from '@vello/core';
import { Badge, Button, DrawerBody, DrawerFooter, DrawerHeader, Drawer, Ink, Notice, RecordPanel, RecordRow, RecordSection, Spacer, Stack, Text, Timeline, type TimelineEntry } from '@vello/web-theme';
import {
  enrollmentTone,
  lineSuspensionMove,
  providerLines,
  providerServicesAuthority,
  reasonsFor,
  reviewMoves,
  screenAllowed,
  serviceHistory,
  vehicleApprovalTone,
  vehicleLabel,
  type ProviderLineView,
  type VehicleLineRow,
} from '../../src/index';
import { useAdmin } from '../session';
import { useRouter } from '../router';
import { useConsole } from '../console';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { documentTypeName } from '../i18n/document-type';
import { CardBody, useCardRead } from './person-card';
import { usePersonFormat } from './person-format';

const SURFACE = 'refusal.admin.provider_services';

/** One confirmed move: the confirmation (with its reason chips when the move asks one), the call, then a notice and a fresh read. */
export function useServiceMove(onDone: () => void): (spec: {
  readonly title: string;
  readonly body: string;
  readonly confirmLabel: string;
  readonly tone: 'primary' | 'danger';
  /** The move's event: REJECT and SUSPEND ask a reason (REJECT must have one). */
  readonly event: string;
  readonly done: string;
  readonly call: (reasonCode: string | undefined) => Promise<Result<unknown, ApiError>>;
  /** No confirmation (a move with no consequence for the provider, like starting the review). */
  readonly direct?: boolean;
}) => Promise<void> {
  const { confirm, toast } = useConsole();
  const { t } = useT();
  const { v, refusal, fail } = useVocab();
  return useCallback(
    async (spec) => {
      const codes = reasonsFor(spec.event);
      const vocab = spec.event === 'REJECT' ? 'provider_services.review_reason' : 'provider_services.suspension_reason';
      let reasonCode: string | undefined;
      if (spec.direct !== true) {
        const answer = await confirm({
          title: spec.title,
          body: spec.body,
          confirmLabel: spec.confirmLabel,
          cancelLabel: t('psvc.confirm.cancel'),
          tone: spec.tone,
          ...(codes.length > 0 ? { reasons: codes.map((c) => v(vocab, c)), reasonLabel: t('psvc.confirm.reason'), reasonsOnly: true } : {}),
        });
        if (!answer.confirmed) return;
        reasonCode = codes.find((c) => v(vocab, c) === answer.reason);
      }
      const res = await spec.call(reasonCode);
      if (res.ok) toast({ message: spec.done, icon: 'check', tone: 'success' });
      else toast({ message: t('psvc.toast.failed', { reason: (refusal(SURFACE, res.error) ?? fail(res.error)).replace(/[.\s]+$/, '') }), icon: 'shieldAlert', tone: 'warning' });
      onDone();
    },
    [confirm, toast, t, v, refusal, fail, onDone],
  );
}

/** The name of a vehicle as a person reads it, or the words for one with no plate. */
function useVehicleName(): (row: Pick<VehicleLineRow, 'facts'> | undefined) => string {
  const { t } = useT();
  return (row) => vehicleLabel(row?.facts) ?? t('psvc.vehicle.unnamed');
}

// ---- the drawer of one request (Serviços em análise) --------------------------------------------------------------------------------

/** What the reviewer reads for one line waiting for the team: the vehicle named, what is reused, what is still to check and the equipment acknowledged. */
function DeltaBlock({ delta, types }: { readonly delta: AdminServiceDeltaView | undefined; readonly types: AdminProviderServicesView['requirementTypes'] }): ReactElement | null {
  const { t, lang } = useT();
  const { v, has } = useVocab();
  if (delta === undefined || !delta.ok) return null;
  // The country pack words its document types; a type only the market's catalog knows reads with the catalog's own label.
  const name = (code: string): string => (has('provider_services.requirement_code', code) ? v('provider_services.requirement_code', code) : documentTypeName(types, code, lang, v));
  return (
    <Stack gap={3}>
      <RecordSection>
        <Text as="h3" role="label">{t('psvc.detail.reused')}</Text>
        {(delta.reused ?? []).length === 0 ? <Text as="p" role="caption" tone="secondary">{t('psvc.detail.reused.empty')}</Text> : (delta.reused ?? []).map((r) => (
          <RecordRow dense key={`${r.code}:${r.documentId}`}><span>{name(r.code)}</span><span className="vello-grow" /><Badge tone="success">{v('provider_services.enrollment_state', 'APPROVED')}</Badge></RecordRow>
        ))}
      </RecordSection>
      <RecordSection>
        <Text as="h3" role="label">{t('psvc.detail.missing')}</Text>
        {(delta.missing ?? []).length === 0 ? <Text as="p" role="caption" tone="secondary">{t('psvc.detail.missing.empty')}</Text> : (delta.missing ?? []).map((m) => (
          <RecordRow dense key={`${m.code}:${m.subject}`}><span>{name(m.code)}</span><span className="vello-grow" /><Badge tone={m.reasonCode === 'REQUIREMENT_PENDING_REVIEW' ? 'warning' : 'danger'}>{v('provider_services.missing_reason', m.reasonCode)}</Badge></RecordRow>
        ))}
      </RecordSection>
      <RecordSection>
        <Text as="h3" role="label">{t('psvc.detail.equipment')}</Text>
        {(delta.equipment ?? []).length === 0 ? <Text as="p" role="caption" tone="secondary">{t('psvc.detail.equipment.empty')}</Text> : (delta.equipment ?? []).map((q) => (
          <RecordRow dense key={q.code}><span>{v('provider_services.equipment_name', q.code)}</span></RecordRow>
        ))}
      </RecordSection>
    </Stack>
  );
}

/**
 * The drawer of one request: the line, its state and revision, the vehicle the provider named with its class, the delta, and ONE primary move (start the
 * review of a submitted line, approve one under review) beside the secondary "do not approve". The provider's record is one click away.
 */
export function ServiceRequestDrawer({ row, onClose, onChanged }: { readonly row: AdminServiceQueueRowView | undefined; readonly onClose: () => void; readonly onChanged: () => void }): ReactElement {
  const { runtime, grants } = useAdmin();
  const { navigate } = useRouter();
  const { t } = useT();
  const { v } = useVocab();
  const fmt = usePersonFormat();
  const vehicleName = useVehicleName();
  const may = providerServicesAuthority(grants);
  const driverId = row?.driverId;
  const read = useCardRead(driverId !== undefined ? () => runtime.providerServices.provider(driverId) : undefined, [runtime, driverId]);
  const done = useCallback(() => {
    read.reload();
    onChanged();
  }, [read, onChanged]);
  const move = useServiceMove(done);
  const name = row?.driverName ?? t('psvc.unnamed');
  const lineName = row !== undefined ? v('provider_services.line', row.line) : '';
  const view = read.state?.status === 'ready' ? read.state.value : undefined;
  const line: ProviderLineView | undefined = view !== undefined && row !== undefined ? providerLines(view).find((l) => l.line === row.line) : undefined;
  const enrollment = line?.enrollment ?? (row !== undefined ? row : undefined);
  const moves = enrollment !== undefined ? reviewMoves(enrollment.state) : {};
  const named = line?.submission !== undefined ? line.vehicles.find((x) => x.vehicleId === line.submission?.vehicleId) : undefined;

  const review = (event: 'START_REVIEW' | 'APPROVE' | 'REJECT'): void => {
    if (row === undefined || enrollment === undefined) return;
    void move({
      event,
      direct: event === 'START_REVIEW',
      title: t(`psvc.confirm.${event}.title`, { name, line: lineName }),
      body: event === 'START_REVIEW' ? '' : t(`psvc.confirm.${event}.body`),
      confirmLabel: v('provider_services.enrollment_event', event),
      tone: event === 'REJECT' ? 'danger' : 'primary',
      done: t(`psvc.toast.${event}`),
      call: (reasonCode) => runtime.providerServices.review(row.driverId, row.line, { event, expectedRevision: enrollment.revision, ...(reasonCode !== undefined ? { reasonCode } : {}) }),
    });
  };

  return (
    <Drawer open={row !== undefined} onClose={onClose} labelledBy="psvc-request-title">
      <DrawerHeader
        titleId="psvc-request-title"
        title={row !== undefined ? `${name} · ${lineName}` : ''}
        subtitle={enrollment !== undefined ? t('psvc.detail.subtitle', { state: v('provider_services.enrollment_state', enrollment.state), revision: String(enrollment.revision) }) : ''}
      />
      <DrawerBody>
        <CardBody what={t('psvc.card.what')} state={read.state} reload={read.reload}>
          {() => (
            <Stack gap={4}>
              <RecordSection>
                <Text as="h3" role="label">{t('psvc.detail.vehicle')}</Text>
                {line?.submission === undefined ? (
                  <Text as="p" role="caption" tone="secondary">{t('psvc.detail.vehicle.none')}</Text>
                ) : (
                  <>
                    <RecordRow dense>
                      <span>{vehicleName(named)}</span>
                      <span className="vello-grow" />
                      {named?.vehicleClass !== undefined && <Badge tone="neutral">{v('service_categories.vehicle_class', named.vehicleClass)}</Badge>}
                    </RecordRow>
                    <Text as="p" role="caption" tone="secondary">{t('psvc.detail.sent', { when: fmt.when(line.submission.actedAt) })}</Text>
                  </>
                )}
              </RecordSection>
              <DeltaBlock delta={line?.submission?.delta} types={view?.requirementTypes} />
              {moves.primary === undefined && <Notice tone="neutral">{t('psvc.detail.idle')}</Notice>}
              {moves.primary !== undefined && !may.mayReview && <Notice tone="neutral">{t('psvc.detail.viewOnly')}</Notice>}
            </Stack>
          )}
        </CardBody>
      </DrawerBody>
      <DrawerFooter>
        {row !== undefined && <Button variant="ghost" onClick={() => navigate('driver_detail', { id: row.driverId })}>{t('psvc.detail.open')}</Button>}
        <Spacer />
        {may.mayReview && moves.secondary !== undefined && view !== undefined && <Button variant="danger" onClick={() => review('REJECT')}>{v('provider_services.enrollment_event', 'REJECT')}</Button>}
        {may.mayReview && moves.primary !== undefined && view !== undefined && <Button variant="primary" onClick={() => review(moves.primary as 'START_REVIEW' | 'APPROVE')}>{v('provider_services.enrollment_event', moves.primary)}</Button>}
      </DrawerFooter>
    </Drawer>
  );
}

// ---- the "Serviços" card of the provider's record -------------------------------------------------------------------------------------

/** The line of the card that says how he works right now: presence, the service he went online in and the vehicle. */
function SessionLine({ view }: { readonly view: AdminProviderServicesView }): ReactElement | null {
  const { t } = useT();
  const { v } = useVocab();
  const s = view.session;
  if (s === undefined || s === null) return null;
  const status = v('provider_services.session_status', s.status);
  const vehicle = s.dutyVehicleId !== null ? (vehicleLabel(view.vehicleDetails?.find((d) => d.vehicleId === s.dutyVehicleId)) ?? t('psvc.vehicle.unnamed')) : undefined;
  const text =
    vehicle === undefined
      ? t('psvc.session.none', { status })
      : s.dutyServiceMode !== null
        ? t('psvc.session.mode', { status, line: v('provider_services.line', s.dutyServiceMode), vehicle })
        : t('psvc.session.vehicleOnly', { status, vehicle });
  return (
    <RecordSection>
      <Text as="h3" role="label">{t('psvc.session.title')}</Text>
      <Text as="p" role="body">{text}</Text>
    </RecordSection>
  );
}

/**
 * Prestadores › ficha › **Serviços** (D883): each line he asked for with its state and revision, every vehicle's approval for it with the move the operator may
 * make (suspend, reinstate, or approve a vehicle that passed its inspection as a class the line takes), the duty session, and the history of both audits.
 */
export function ProviderServicesCard({ driverId, providerName }: { readonly driverId: string; readonly providerName: string }): ReactElement {
  const { runtime, grants } = useAdmin();
  const { navigate } = useRouter();
  const { t } = useT();
  const { v, reason, has } = useVocab();
  const fmt = usePersonFormat();
  const vehicleName = useVehicleName();
  const may = providerServicesAuthority(grants);
  const read = useCardRead(() => runtime.providerServices.provider(driverId), [runtime, driverId]);
  const move = useServiceMove(read.reload);

  const lineMove = (line: ProviderLineView, event: 'SUSPEND' | 'REINSTATE'): void => {
    const lineName = v('provider_services.line', line.line);
    void move({
      event,
      title: t(`psvc.confirm.${event}.title`, { name: providerName, line: lineName }),
      body: t(`psvc.confirm.${event}.body`),
      confirmLabel: v('provider_services.enrollment_event', event),
      tone: event === 'SUSPEND' ? 'danger' : 'primary',
      done: t(`psvc.toast.${event}`),
      call: (reasonCode) => runtime.providerServices.suspendLine(driverId, line.line, { event, expectedRevision: line.enrollment.revision, ...(reasonCode !== undefined ? { reasonCode } : {}) }),
    });
  };
  const vehicleMove = (line: ProviderLineView, row: VehicleLineRow, event: 'SUSPEND' | 'REINSTATE' | 'APPROVE'): void => {
    const lineName = v('provider_services.line', line.line);
    const vehicle = vehicleName(row);
    const vehicleClass = row.facts?.latestPassedInspection?.vehicleClass ?? row.facts?.inspectionExempt?.vehicleClass ?? row.vehicleClass;
    void move({
      event,
      title: t(`psvc.confirm.vehicle.${event}.title`, { vehicle, line: lineName }),
      body: t(`psvc.confirm.vehicle.${event}.body`, { vehicleClass: vehicleClass !== undefined ? v('service_categories.vehicle_class', vehicleClass) : '' }),
      confirmLabel: event === 'APPROVE' ? t('psvc.vehicle.approve') : v('provider_services.enrollment_event', event),
      tone: event === 'SUSPEND' ? 'danger' : 'primary',
      done: t(`psvc.toast.vehicle.${event}`),
      call: (reasonCode) =>
        event === 'APPROVE'
          ? runtime.providerServices.approveVehicle(driverId, line.line, row.vehicleId)
          : runtime.providerServices.suspendVehicle(driverId, line.line, row.vehicleId, { event, expectedRevision: row.approval?.revision ?? 1, ...(reasonCode !== undefined ? { reasonCode } : {}) }),
    });
  };

  const vehicleAction = (line: ProviderLineView, row: VehicleLineRow): ReactNode => {
    if (row.move === 'APPROVE') return may.mayReview ? <Button size="sm" variant="secondary" onClick={() => vehicleMove(line, row, 'APPROVE')}>{t('psvc.vehicle.approve')}</Button> : null;
    if (row.move === 'SUSPEND' || row.move === 'REINSTATE') {
      const event = row.move;
      return may.maySuspend ? <Button size="sm" variant={event === 'SUSPEND' ? 'ghost' : 'secondary'} onClick={() => vehicleMove(line, row, event)}>{v('provider_services.enrollment_event', event)}</Button> : null;
    }
    return null;
  };

  const historyEntries = (view: AdminProviderServicesView): readonly TimelineEntry[] =>
    serviceHistory(view).map((h) => {
      const line = v('provider_services.line', h.line);
      const event = h.kind === 'ENROLLMENT' ? v('provider_services.enrollment_audit_event', h.event) : v('provider_services.vehicle_approval_audit_event', h.event);
      const vehicle = h.vehicleId !== undefined ? (vehicleLabel(view.vehicleDetails?.find((d) => d.vehicleId === h.vehicleId)) ?? t('psvc.vehicle.unnamed')) : undefined;
      const operatorWhy = h.operatorReasonCode !== undefined ? (h.event === 'REJECT' ? v('provider_services.review_reason', h.operatorReasonCode) : h.event === 'SUSPEND' ? v('provider_services.suspension_reason', h.operatorReasonCode) : undefined) : undefined;
      // D898: an automatic move says why when its reason has words (the line back with the renewed document)
      const why = operatorWhy ?? (has('provider_services.system_move_reason', h.systemReasonCode) ? v('provider_services.system_move_reason', h.systemReasonCode) : undefined);
      const tone: TimelineEntry['tone'] = h.toState === 'SUSPENDED' || h.toState === 'REJECTED' || h.toState === 'EXPIRED' ? 'danger' : h.toState === 'SUBMITTED' || h.toState === 'IN_REVIEW' ? 'warning' : 'neutral';
      return {
        id: h.key,
        tone,
        title: vehicle !== undefined ? t('psvc.history.vehicleEntry', { line, vehicle, event }) : t('psvc.history.entry', { line, event }),
        ...(why !== undefined ? { detail: why } : {}),
        meta: `${fmt.when(h.actedAt)} · ${v('provider_services.move_source', h.source)}`,
      };
    });

  return (
    <RecordPanel
      title={t('psvc.card.title')}
      actions={screenAllowed(grants, 'provider_services') ? <Button variant="ghost" size="sm" onClick={() => navigate('provider_services')}>{t('psvc.card.queue')}</Button> : undefined}
    >
      <CardBody what={t('psvc.card.what')} state={read.state} reload={read.reload}>
        {(view) => (
          <>
            <SessionLine view={view} />
            {view.enrollments.length === 0 && <Text as="p" role="caption" tone="secondary">{t('psvc.card.empty')}</Text>}
            {providerLines(view).map((line) => {
              const suspension = lineSuspensionMove(line.enrollment.state);
              return (
                <RecordSection key={line.line} role="group" aria-label={v('provider_services.line', line.line)}>
                  <RecordRow>
                    <Text as="h3" role="label">{v('provider_services.line', line.line)}</Text>
                    <Badge tone={enrollmentTone(line.enrollment.state)}>{v('provider_services.enrollment_state', line.enrollment.state)}</Badge>
                    <span className="vello-grow" />
                    {may.maySuspend && suspension !== undefined && (
                      <Button size="sm" variant={suspension === 'SUSPEND' ? 'ghost' : 'secondary'} onClick={() => lineMove(line, suspension)}>{v('provider_services.enrollment_event', suspension)}</Button>
                    )}
                  </RecordRow>
                  <Text as="p" role="caption" tone="secondary">{t('psvc.line.since', { revision: String(line.enrollment.revision), when: fmt.when(line.enrollment.updatedAt) })}</Text>
                  {line.rule !== undefined && !line.rule.enabled && <Text as="p" role="caption" tone="secondary">{t('psvc.line.notRunning')}</Text>}
                  {line.vehicles.length === 0 && <Text as="p" role="caption" tone="secondary">{t('psvc.vehicle.none')}</Text>}
                  {line.vehicles.map((row) => (
                    <div key={row.vehicleId}>
                      <RecordRow dense>
                        <span>{vehicleName(row)}</span>
                        {row.onDutyHere && <Badge tone="neutral">{t('psvc.vehicle.onDuty')}</Badge>}
                        <span className="vello-grow" />
                        {row.approval !== undefined ? (
                          <Badge tone={vehicleApprovalTone(row.approval.state)}>{v('provider_services.vehicle_approval_state', row.approval.state)}</Badge>
                        ) : (
                          <Ink tone="secondary">{t('psvc.vehicle.noApproval')}</Ink>
                        )}
                        {vehicleAction(line, row)}
                      </RecordRow>
                      {row.facts?.latestPassedInspection !== undefined && (
                        <Text as="p" role="caption" tone="secondary">{t('psvc.vehicle.inspected', { vehicleClass: v('service_categories.vehicle_class', row.facts.latestPassedInspection.vehicleClass) })}</Text>
                      )}
                      {row.facts?.latestPassedInspection === undefined && row.facts?.inspectionExempt !== undefined && (
                        <Text as="p" role="caption" tone="secondary">{t('psvc.vehicle.inspectionExempt', { vehicleClass: v('service_categories.vehicle_class', row.facts.inspectionExempt.vehicleClass) })}</Text>
                      )}
                      {row.blocked !== undefined && row.blocked !== 'ENROLLMENT_NOT_APPROVED' && <Text as="p" role="caption" tone="secondary">{reason(row.blocked)}</Text>}
                    </div>
                  ))}
                </RecordSection>
              );
            })}
            <RecordSection>
              <Text as="h3" role="label">{t('psvc.history.title')}</Text>
              {serviceHistory(view).length === 0 ? <Text as="p" role="caption" tone="secondary">{t('psvc.history.empty')}</Text> : <Timeline entries={historyEntries(view)} />}
            </RecordSection>
          </>
        )}
      </CardBody>
    </RecordPanel>
  );
}
